import { FC } from 'react';

import { useRecoilValue } from 'recoil';

import { BugoAuthRouterLazy } from '@route/lazyRouters';
import { BugoNavBar } from '@service/bugo/components/BugoNavBar';
import ErrorPage from '@service/bugo/page/ErrorPage';
import { bugoNavBarUseAtom } from '@service/bugo/state';
import { lazyRoute } from '@shared/components/RouteFallback';
import { useAuth } from '@shared/state/hooks/useAuth';
import { lazyWithReload } from '@shared/utils/lazyWithReload';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import BugoIdRouter from './BugoIdRouter';
import BugoMemberIdRouter from './BugoMemberIdRouter';

//* 조문객 기본 번들에는 부고장(bugo/:id)·상주 링크(member/:id)만 싣는다.
//* 로그인·가입·약관·문의 등 계정 화면은 한 청크("bugo-account")로 묶어 처음 열 때 받고,
//* 상주·지도사 로그인 영역(BugoAuthRouter)은 별도 청크("bugo-auth"). ErrorPage는 복구용이라 기본 번들에 둔다.
const accountPage = <T,>(
  pick: (m: T) => React.ComponentType<any>,
  importer: () => Promise<T>,
) => lazyWithReload(() => importer().then((m) => ({ default: pick(m) })));
const BugoEnquiryPage = accountPage(
  (m) => m.default,
  () =>
    import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/BugoEnquiryPage'),
);
const BugoFAQPage = accountPage(
  (m) => m.default,
  () => import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/BugoFAQPage'),
);
const BugoLoginPage = accountPage(
  (m) => m.BugoLoginPage,
  () => import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/BugoLoginPage'),
);
const BugoSignupPage = accountPage(
  (m) => m.BugoSignupPage,
  () =>
    import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/BugoSignupPage'),
);
const CiAuthNavigatorPage = accountPage(
  (m) => m.CiAuthNavigatorPage,
  () =>
    import(
      /* webpackChunkName: "bugo-account" */ '@service/bugo/page/CiAuthNavigatorPage'
    ),
);
const FindMyIdPage = accountPage(
  (m) => m.FindMyIdPage,
  () => import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/FindMyIdPage'),
);
const SignupInitPage = accountPage(
  (m) => m.SignupInitPage,
  () =>
    import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/SignupInitPage'),
);
const TermsAgreementPage = accountPage(
  (m) => m.TermsAgreementPage,
  () =>
    import(
      /* webpackChunkName: "bugo-account" */ '@service/bugo/page/TermsAgreementPage'
    ),
);
const TermsPage = accountPage(
  (m) => m.TermsPage,
  () => import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/TermsPage'),
);
const UnitTestPage = accountPage(
  (m) => m.default,
  () => import(/* webpackChunkName: "bugo-account" */ '@service/bugo/page/UnitTestPage'),
);

export const BugoRouter: FC = function BugoRouter() {
  const { pathname, search } = useLocation();
  const bugoNavBarUse = useRecoilValue(bugoNavBarUseAtom);
  const notUseNavBar =
    pathname.endsWith('login') || pathname.endsWith('error') || !bugoNavBarUse;

  const { isAuthenticated } = useAuth();
  if (
    pathname !== '/login' &&
    pathname !== '/' &&
    pathname !== '/signup/init' &&
    !isAuthenticated
  ) {
    sessionStorage.setItem('redirectUrlAfterBugoLogin', pathname + search);
  }

  //! 예외 케이스 정의가 필요함
  return (
    <div className="max-w-inherit">
      <BugoNavBar />
      <div className={`max-w-inherit ${notUseNavBar ? '' : 'pt-14'}`}>
        {/* lazy 페이지는 라우트마다 lazyRoute()로 — 전체를 Suspense 하나로 묶지 말 것(사유: RouteFallback.tsx) */}
        <Routes>
          <Route path="faq" element={lazyRoute(<BugoFAQPage />)} />
          <Route path="enquiry" element={lazyRoute(<BugoEnquiryPage />)} />
          <Route path="find-id-password" element={lazyRoute(<FindMyIdPage />)} />
          <Route
            path="signup/terms-agreement"
            element={lazyRoute(<TermsAgreementPage />)}
          />
          <Route path="check-ci" element={lazyRoute(<CiAuthNavigatorPage />)} />
          <Route path="terms" element={lazyRoute(<TermsPage />)} />
          <Route path="unit-test" element={lazyRoute(<UnitTestPage />)} />
          <Route path="error" element={<ErrorPage />}></Route>
          <Route path="signup/init" element={lazyRoute(<SignupInitPage />)} />
          <Route path="bugo/:bugoId/*" element={<BugoIdRouter />} />
          <Route path="member/:memberId/*" element={<BugoMemberIdRouter />} />
          {/* logout required */}
          <Route
            path="login"
            element={lazyRoute(
              !isAuthenticated ? <BugoLoginPage /> : <Navigate to="/" />,
            )}
          />
          <Route
            path="signup"
            element={lazyRoute(
              !isAuthenticated ? <BugoSignupPage /> : <Navigate to="/" />,
            )}
          />

          {/* login required */}
          <Route
            path="*"
            element={lazyRoute(
              isAuthenticated ? <BugoAuthRouterLazy /> : <Navigate to="/login" />,
            )}
          />
        </Routes>
      </div>
    </div>
  );
};
